/**
 * Couldn't Be Graded screen (07) — per PRD §12 + wireframe
 * `07_couldnt_be_graded_*`.
 *
 * V1 shell scope: the grade pipeline doesn't exist yet, so this screen is
 * not yet reachable via real grade-failure paths. Once the GradeService
 * 3-tier retry exhausts (PRD §11.3), it'll route here. Until then the
 * screen is implemented for visual completeness and tested via direct
 * navigation.
 *
 * Layout: warning banner + per-street transcript list (read-only) +
 * Continue → CTA that proceeds to the next hand (the hand is preserved
 * in the session — PRD: "its presence in the session is preserved …
 * the session can complete normally").
 */

import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { sessionManager } from '@/services/sessionManager';
import { isTerminal } from '@/lib/engine/stateMachine';
import type { ActionRecord, HandState } from '@/types/handState';
import type { Session } from '@/types/session';
import { cn } from '@/lib/utils';

export function CouldntBeGradedScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const errorMessage = (location.state as { errorMessage?: string } | null)
    ?.errorMessage;
  const [session, setSession] = useState<Session | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setSession(sessionManager.loadActiveSession());
    setLoaded(true);
  }, []);

  if (!loaded) return null;
  if (!session) return <Navigate to="/" replace />;
  if (!session.activeHand) return <Navigate to="/config" replace />;
  if (!isTerminal(session.activeHand)) return <Navigate to="/play" replace />;

  const state = session.activeHand;
  const handNumber = 1; // see PostHandGrade — depends on persisted counter
  const handsTotal = session.config.handsCount;

  const handleContinue = () => {
    sessionManager.startNextHand();
    navigate('/play');
  };

  return (
    <main className="flex min-h-dvh flex-col bg-surface-app text-text-primary">
      {/* Header */}
      <div className="shrink-0 border-b border-border bg-surface-app px-4 py-3">
        <div className="mx-auto flex max-w-2xl items-center justify-between text-xs">
          <button
            type="button"
            onClick={() => navigate('/resolution')}
            className="text-text-secondary hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-border-focus"
          >
            ‹ Back
          </button>
          <div className="text-text-secondary">
            Hand {handNumber} of {handsTotal}
          </div>
        </div>
      </div>

      {/* Warning banner */}
      <div className="border-b border-border bg-surface-elevated px-4 py-5 text-center">
        <div className="mx-auto max-w-2xl">
          <h1 className="mb-1 text-base font-bold uppercase tracking-wider text-state-error">
            ⚠ Couldn't be graded
          </h1>
          <p className="text-sm text-text-secondary">Auto-retry exhausted.</p>
          <p className="mt-1 text-xs text-text-muted">
            Your transcript is shown below. Continue when ready.
          </p>
          {errorMessage && (
            <details className="mt-3 text-left">
              <summary className="cursor-pointer text-[10px] uppercase tracking-wider text-text-muted hover:text-text-secondary">
                Show error detail
              </summary>
              <pre className="mt-2 max-h-40 overflow-auto rounded-sm border border-border bg-surface-app p-2 text-left font-mono text-[11px] leading-snug text-text-secondary">
                {errorMessage}
              </pre>
            </details>
          )}
        </div>
      </div>

      {/* Transcript list */}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-2xl px-4 py-4">
          <TranscriptList state={state} />
        </div>
      </div>

      {/* Continue CTA */}
      <div className="flex min-h-[5rem] items-center border-t border-border bg-surface-elevated px-4">
        <div className="mx-auto flex w-full max-w-2xl justify-end">
          <button
            type="button"
            onClick={handleContinue}
            className={cn(
              'rounded-md bg-brand-primary px-5 py-2.5 text-sm font-semibold',
              'text-text-on-brand hover:bg-brand-primary-hover',
              'focus:outline-none focus:ring-2 focus:ring-border-focus focus:ring-offset-1',
            )}
          >
            Continue →
          </button>
        </div>
      </div>
    </main>
  );
}

// =============================================================================
// Per-street transcript display (mirrors PostHandGrade.TranscriptList)
// =============================================================================

function TranscriptList({ state }: { state: HandState }) {
  const heroByStreet = new Map<string, ActionRecord[]>();
  state.history.forEach((rec) => {
    if (rec.actor !== 'hero') return;
    if (!heroByStreet.has(rec.street)) heroByStreet.set(rec.street, []);
    heroByStreet.get(rec.street)!.push(rec);
  });

  const streets: Array<'flop' | 'turn' | 'river'> = ['flop', 'turn', 'river'];
  const anyTranscript = state.history.some(
    (r) => r.actor === 'hero' && r.transcript,
  );

  return (
    <>
      {!anyTranscript && (
        <div className="mb-4 rounded-md border border-border bg-surface-elevated p-3 text-xs text-text-muted">
          No transcripts captured.
        </div>
      )}
      {streets.map((street) => {
        const records = heroByStreet.get(street) ?? [];
        if (records.length === 0) return null;
        return (
          <div key={street} className="mb-5">
            <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-text-muted">
              {street}
            </div>
            <div className="space-y-2">
              {records.map((rec) => (
                <blockquote
                  key={rec.index}
                  className={cn(
                    'rounded-md border border-border bg-surface-elevated p-3 text-sm',
                    rec.transcript
                      ? 'text-text-secondary'
                      : 'text-text-muted italic',
                  )}
                >
                  {rec.transcript ??
                    '(no transcript captured for this decision)'}
                </blockquote>
              ))}
            </div>
          </div>
        );
      })}
      <p className="mt-6 text-[10px] uppercase tracking-wider text-text-muted">
        Transcripts are read-only.
      </p>
    </>
  );
}
